import CardsWithIntro from 'components/slices/CardsWithIntro'
import React, { useRef, useState, useEffect } from 'react'
import { fetchIntegrationsCardCategory } from 'services/clientApi'

const LIMIT = 6

const IntegrationsCardsWithIntro = ({
  cards,
  buttonText,
  res,
  filterTagIds = [],
  excludeIds = [],
  ...rest
}) => {
  /**
   * Some cards are not renamed correctly
   */
  const changeCardsText = (oldCards) =>
    oldCards.map((card) => ({
      ...card,
      link: { ...card.link, text: buttonText ? buttonText : undefined },
    }))

  const defaultCards = cards ? changeCardsText(cards) : []
  const [integrationsCards, setIntegrationsCards] = useState(defaultCards)
  const [page, setPage] = useState(1)
  const categoryId = useRef(res?.categoryId)
  const count = useRef(res?.count)

  /**
   * Fetch state machine:
   * - FILTERED: fetch with tag filter, track fetched IDs
   * - UNFILTERED: normal pagination, exclude priority IDs
   *
   * When filterTagIds is provided, we fetch with tag filter, and track fetched IDs
   * first, then fallback to normal pagination, exclude priority IDs when there are
   * no more cards with tag filter.
   */
  const fetchState = useRef({
    phase: filterTagIds?.length > 0 ? 'FILTERED' : 'UNFILTERED',
    page: 1,
    priorityIds: [], // IDs fetched during FILTERED phase
  })

  const removeDuplicates = (cards) =>
    cards.filter((card, i, arr) => arr.findIndex((c) => c.id === card.id) === i)

  const buildFetchOptions = (state) => {
    const options = { page: state.page, limit: LIMIT }

    if (state.phase === 'FILTERED') {
      options.integrationTagIdsFilter = filterTagIds
      if (excludeIds.length > 0) options.excludeIds = excludeIds
    } else {
      const allExcludeIds = [...excludeIds, ...state.priorityIds]
      if (allExcludeIds.length > 0) options.excludeIds = allExcludeIds
    }

    return options
  }

  const fetchCards = async ({ currentCards, maxCards }) => {
    const state = fetchState.current
    const response = await fetchIntegrationsCardCategory(buildFetchOptions(state))
    const newCards = response?.integrations || []

    let result = removeDuplicates([...currentCards, ...newCards])

    // State transitions
    if (state.phase === 'FILTERED') {
      state.priorityIds.push(...newCards.map((c) => c.id))

      if (newCards.length < LIMIT) {
        // Transition: FILTERED -> UNFILTERED
        state.phase = 'UNFILTERED'
        state.page = 1

        if (result.length < maxCards) {
          return fetchCards({ currentCards: result, maxCards })
        }
      } else {
        state.page++
      }
    } else {
      state.page++
    }

    return changeCardsText(result.slice(0, maxCards))
  }

  const onChange = async (value) => {
    const newCards = (categoryId.current = value)
    count.current = newCards?.count
    setPage(1)
    const editedNewCards = newCards ? changeCardsText(newCards) : []
    setIntegrationsCards(editedNewCards)
  }

  const loadMore = async () => {
    const pageVal = page + 1
    const newCards = await fetchCards({
      currentCards: integrationsCards,
      maxCards: pageVal * LIMIT,
    })
    setIntegrationsCards(newCards)
    setPage(pageVal)
  }

  const initCards = async () => {
    const newCards = await fetchCards({
      currentCards: integrationsCards,
      maxCards: 1 * LIMIT,
    })
    setIntegrationsCards(newCards)
  }

  useEffect(() => {
    initCards()
  }, [])

  return (
    <CardsWithIntro
      bottomLink
      buttonText={buttonText}
      category={categoryId.current}
      onChange={onChange}
      cards={integrationsCards}
      hasMore={page * LIMIT < count.current}
      onClick={loadMore}
      {...rest}
    />
  )
}

export default IntegrationsCardsWithIntro
